/**
 * The small pieces every lab panel needs, in one place.
 *
 * Kept together so a state does not read green in the run view and amber in the pool view,
 * and so the JSON editors all refuse to save unparseable text the same way.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { HbrIcon, HbrOption, HbrSelect, HbrSpinner, HbrTag } from '@harbor/elements-react-standalone';
import { caretDown, caretRight, question } from '@harbor/elements/icons';
import StbButton from '../security-toolbox-ui/StbButton';
import { asSingleValue } from '../../support/utils';
import { formatDcloudDate } from '../../support/dcloudService';
import { LabOption, LabSentiment } from '../../support/labService';
import { LabJsonSchema } from '../../support/labSpecSchema';

/** Reads an error off anything a fetch or a mutation threw. */
export const labErrorMessage = (error: unknown, fallback = 'Unable to reach the lab controller.') =>
  (error as Error)?.message || fallback;

export const LabLoading: React.FC<{ label?: string }> = ({ label = 'Loading...' }) => (
  <div className="flex items-center gap-2 p-4 text-sm text-gray-600">
    <HbrSpinner size="small" />
    {label}
  </div>
);

export const LabMessage: React.FC<{ tone?: 'muted' | 'warning'; children: React.ReactNode }> = ({
  tone = 'muted',
  children,
}) => (
  <p className={`m-0 p-4 text-sm ${tone === 'warning' ? 'text-amber-800' : 'text-gray-600'}`}>
    {children}
  </p>
);

/** Loading / error / empty in the order they matter, so every panel behaves the same. */
export const LabPanelState: React.FC<{
  loading?: boolean;
  error?: unknown;
  empty?: boolean;
  emptyMessage?: string;
  loadingLabel?: string;
  children: React.ReactNode;
}> = ({ loading, error, empty, emptyMessage = 'Nothing here yet.', loadingLabel, children }) => {
  if (loading) return <LabLoading label={loadingLabel} />;
  if (error) return <LabMessage tone="warning">{labErrorMessage(error)}</LabMessage>;
  if (empty) return <LabMessage>{emptyMessage}</LabMessage>;

  return <>{children}</>;
};

export const LabCard: React.FC<{
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, actions, className = '', children }) => (
  <section className={`rounded-md border border-gray-200 bg-white p-4 ${className}`}>
    {(title || actions) && (
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          {title && <h3 className="m-0 text-base font-semibold">{title}</h3>}
          {subtitle && <div className="mt-1 text-xs text-gray-500">{subtitle}</div>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    )}
    {children}
  </section>
);

/** A state, in a tag, with a sentiment somebody else decided. */
export const LabStatusTag: React.FC<{
  label?: string | null;
  sentiment?: LabSentiment;
  size?: 'small' | 'medium';
}> = ({ label, sentiment = 'info', size = 'small' }) => (
  <HbrTag size={size} sentiment={sentiment as any}>
    {label || 'unknown'}
  </HbrTag>
);

export const LabDate: React.FC<{ value?: string | null; fallback?: string }> = ({
  value,
  fallback = '-',
}) => <span className="whitespace-nowrap">{value ? formatDcloudDate(value) : fallback}</span>;

/** Seconds as something a person reads. Durations here run from milliseconds to hours. */
export const labDuration = (seconds?: number | null): string => {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;

  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

export const LabProgressBar: React.FC<{
  percent: number;
  label?: string;
  sentiment?: LabSentiment;
}> = ({ percent, label, sentiment = 'info' }) => {
  const clamped = Math.max(0, Math.min(100, Math.round(percent || 0)));
  const fill =
    sentiment === 'success'
      ? 'bg-green-600'
      : sentiment === 'danger'
        ? 'bg-red-600'
        : sentiment === 'warning'
          ? 'bg-amber-500'
          : 'bg-blue-600';

  return (
    <div className="min-w-[120px]">
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
        <div className={`h-2 rounded-full ${fill}`} style={{ width: `${clamped}%` }} />
      </div>
      <div className="mt-1 text-xs text-gray-500">{label || `${clamped}%`}</div>
    </div>
  );
};

export const LabFieldRow: React.FC<{ children: React.ReactNode; columns?: 1 | 2 | 3 }> = ({
  children,
  columns = 2,
}) => (
  <div
    className={`grid grid-cols-1 gap-4 ${
      columns === 3 ? 'md:grid-cols-3' : columns === 2 ? 'md:grid-cols-2' : ''
    }`}
  >
    {children}
  </div>
);

/**
 * A choice from a closed list.
 *
 * `allowEmpty` exists for the fields that have no default in the schema - `blast_radius`
 * most of all. Rendering the first option as though it were chosen is how a step ends up
 * claiming a blast radius nobody stated, so an unset field shows an unset select.
 */
export function LabOptionSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  helpText,
  allowEmpty = false,
  emptyLabel = 'Not set',
  disabled = false,
  hideLabel = false,
}: {
  label: string;
  value: T | null | undefined;
  options: LabOption<T>[];
  onChange: (value: T) => void;
  helpText?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
  disabled?: boolean;
  /**
   * Drops the visible label, for a control in a table of them.
   *
   * The key/value rows are the reason: a label above every cell of every row is the same three
   * words repeated down the column, and reading a row means stepping over them. The label is still
   * required and still announced - it moves to the column heading and to `aria-label`.
   */
  hideLabel?: boolean;
}) {
  const hint = useMemo(
    () => options.find((option) => option.value === value)?.hint,
    [options, value],
  );

  return (
    <HbrSelect
      value={(value as string) || ''}
      size="small"
      disabled={disabled}
      aria-label={hideLabel ? label : undefined}
      onHbr-change={(evt: any) => onChange(asSingleValue(evt.target.value) as T)}
    >
      {allowEmpty && <HbrOption value="">{emptyLabel}</HbrOption>}
      {options.map((option) => (
        <HbrOption value={option.value} key={option.value}>
          {option.label}
        </HbrOption>
      ))}
      {!hideLabel && <div slot="label">{label}</div>}
      {(helpText || hint) && <div slot="help-text">{helpText || hint}</div>}
    </HbrSelect>
  );
}

/** Chosen so it cannot collide with a platform, a role, or anything else a person would type. */
const OTHER_SENTINEL = ' other';

/**
 * A text input, without pulling MUI into this file.
 *
 * `LabShared` is imported by every lab panel including the read-only ones, and the styling here
 * matches the JSON editor's textarea rather than the MUI fields, so a suggested value and a typed one
 * do not look like different kinds of answer.
 */
const TextFieldLike: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  helpText?: string;
  placeholder?: string;
  disabled?: boolean;
}> = ({ label, value, onChange, helpText, placeholder, disabled }) => (
  <label className="flex flex-col gap-1 text-sm">
    <span className="font-medium text-gray-700">{label}</span>
    <input
      className="w-full rounded border border-gray-300 p-2 text-sm"
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(evt) => onChange(evt.target.value)}
    />
    {helpText && <span className="text-xs text-gray-500">{helpText}</span>}
  </label>
);

/**
 * A value that is usually one somebody already used.
 *
 * For the columns that are free text in the schema but an enumeration in practice: a device's
 * platform and its role are typed by hand today, so the same box is `windows`, `Windows`, and
 * `Windows Server 2022` across three sections, and grouping by either is useless. Offering what the
 * library already contains makes the consistent answer the easy one.
 *
 * Free text stays reachable, because the list is *what has been used*, not what is allowed - the
 * first device with a new platform has nothing to pick, and a select alone would make it unauthorable.
 * A value that is not in the list keeps the text box open rather than reading as unset.
 */
export const LabSuggestedTextField: React.FC<{
  label: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  /** Values already in use. Empty falls back to a plain text field. */
  suggestions: string[];
  helpText?: string;
  emptyLabel?: string;
  otherLabel?: string;
  placeholder?: string;
  disabled?: boolean;
}> = ({
  label,
  value,
  onChange,
  suggestions,
  helpText,
  emptyLabel = 'Not set',
  otherLabel = 'Something else...',
  placeholder,
  disabled = false,
}) => {
  const current = value || '';
  const known = useMemo(
    () => suggestions.filter(Boolean).filter((entry, index, all) => all.indexOf(entry) === index),
    [suggestions],
  );

  // Sticky rather than derived, so clearing the text box does not throw the author back to the
  // select mid-word. Starts open for a value nothing suggests, which is how an existing row's own
  // answer stays visible.
  const [other, setOther] = useState(() => !!current && !known.includes(current));

  if (!known.length) {
    return (
      <TextFieldLike
        label={label}
        value={current}
        onChange={onChange}
        helpText={helpText}
        placeholder={placeholder}
        disabled={disabled}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <LabOptionSelect
        label={label}
        value={other ? OTHER_SENTINEL : current}
        options={[
          ...known.map((entry) => ({ value: entry, label: entry })),
          { value: OTHER_SENTINEL, label: otherLabel, hint: 'Type a value nothing here uses yet.' },
        ]}
        onChange={(next) => {
          if (next === OTHER_SENTINEL) {
            setOther(true);
            onChange('');
            return;
          }

          setOther(false);
          onChange(next);
        }}
        allowEmpty
        emptyLabel={emptyLabel}
        helpText={helpText}
        disabled={disabled}
      />

      {other && (
        <TextFieldLike
          label={`${label}, in full`}
          value={current}
          onChange={onChange}
          helpText="Used by nothing else yet. It joins the list above once saved."
          placeholder={placeholder}
          disabled={disabled}
        />
      )}
    </div>
  );
};

/**
 * A key nobody typed.
 *
 * Every key in the lab schema is derived from a name, because a key is what compiled rows are named
 * from and a typo in one is a step that refers to nothing. Shown rather than hidden: it is what
 * appears in the run views, the health reports and the compile errors, so an author has to be able to
 * recognise it - and it is frozen once saved, which is only comprehensible if it was visible before.
 */
export const LabDerivedKey: React.FC<{
  value: string;
  /** True once the row exists, when the key can no longer follow the name. */
  frozen?: boolean;
  label?: string;
  /** What the key is used for, in this table. */
  usage?: string;
  emptyLabel?: string;
}> = ({
  value,
  frozen = false,
  label = 'Key',
  usage = 'Compiled rows are named from it.',
  emptyLabel = 'not yet derived',
}) => (
  <div className="flex flex-wrap items-baseline gap-2 text-xs text-gray-500">
    <span className="uppercase tracking-wide">{label}</span>
    <span className="font-mono text-sm text-gray-700">{value || emptyLabel}</span>
    <span>
      {frozen ? `Kept as saved. ${usage} Renaming does not change it.` : `Derived from the name. ${usage}`}
    </span>
  </div>
);

/**
 * A choice from a table.
 *
 * The same shape as `LabOptionSelect`, for the fields whose options are rows somebody authored
 * rather than a list in the code: device types, execution methods, a scenario's devices. Values
 * are numeric ids and the select speaks strings, so the conversion lives here once instead of in
 * every form that needs it.
 *
 * Unset is a first-class answer for most of these - an item with no execution method falls back
 * to a plain shell command, which is a decision, not a missing field - so `allowEmpty` defaults on.
 */
export function LabRowSelect<T extends { id: number }>({
  label,
  value,
  rows,
  onChange,
  labelFor,
  hintFor,
  helpText,
  allowEmpty = true,
  emptyLabel = 'Not set',
  disabled = false,
}: {
  label: string;
  value: number | null | undefined;
  rows: T[];
  onChange: (value: number | null) => void;
  labelFor: (row: T) => string;
  hintFor?: (row: T) => string | undefined;
  helpText?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
  disabled?: boolean;
}) {
  const hint = useMemo(() => {
    const row = rows.find((candidate) => candidate.id === value);

    return row && hintFor ? hintFor(row) : undefined;
  }, [rows, value, hintFor]);

  return (
    <HbrSelect
      value={value === null || value === undefined ? '' : String(value)}
      size="small"
      disabled={disabled}
      onHbr-change={(evt: any) => {
        const next = asSingleValue(evt.target.value);
        onChange(next === '' || next === undefined ? null : Number(next));
      }}
    >
      {allowEmpty && <HbrOption value="">{emptyLabel}</HbrOption>}
      {rows.map((row) => (
        <HbrOption value={String(row.id)} key={row.id}>
          {labelFor(row)}
        </HbrOption>
      ))}
      <div slot="label">{label}</div>
      {(helpText || hint) && <div slot="help-text">{helpText || hint}</div>}
    </HbrSelect>
  );
}

/** How many products a select lists at once. Above this, the filter box is the way through. */
const PRODUCT_OPTION_LIMIT = 50;

/**
 * Which Cisco product something is, out of two thousand.
 *
 * Shared by the device form and the item form because it is the same question in both - the device
 * says what the box is, the item says which product its spec was written for, and an attach compares
 * the two - and because a control this fiddly rendered twice is two things to keep looking alike.
 *
 * Two thousand rows is not a dropdown, so the filter above it is what makes this one: the options are
 * whatever matches, capped. The chosen product is always in the list even when it does not match,
 * because a select that cannot render its own value shows blank and reads as unset.
 */
export const LabProductSelect: React.FC<{
  value: number | null | undefined;
  /** The name snapshot on the row, so a chosen product still reads as chosen under a filter. */
  name?: string | null;
  products?: { id: number; name: string }[];
  loading?: boolean;
  onChange: (product: { id: number; name: string } | null) => void;
  label?: string;
  helpText?: string;
  disabled?: boolean;
}> = ({
  value,
  name,
  products,
  loading = false,
  onChange,
  label = 'Cisco product',
  helpText,
  disabled = false,
}) => {
  const [search, setSearch] = useState('');
  const catalogue = useMemo(() => products || [], [products]);

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return needle
      ? catalogue.filter((product) => (product.name || '').toLowerCase().includes(needle))
      : catalogue;
  }, [catalogue, search]);

  const options = useMemo(() => {
    const shown = matches.slice(0, PRODUCT_OPTION_LIMIT);

    if (value && !shown.some((product) => product.id === value)) {
      return [{ id: value, name: name || `product ${value}` }, ...shown];
    }

    return shown;
  }, [matches, value, name]);

  return (
    <div className="flex flex-col gap-2">
      {catalogue.length > PRODUCT_OPTION_LIMIT && (
        <TextFieldLike
          label="Filter products"
          value={search}
          onChange={setSearch}
          placeholder="Two or three letters of the name"
          helpText={
            loading
              ? 'Loading the product catalogue...'
              : `Showing ${Math.min(matches.length, PRODUCT_OPTION_LIMIT)} of ${matches.length}.`
          }
          disabled={disabled}
        />
      )}

      <LabRowSelect
        label={label}
        value={value}
        rows={options}
        onChange={(id) => onChange(id ? options.find((product) => product.id === id) || null : null)}
        labelFor={(product) => product.name}
        emptyLabel={
          loading ? 'Loading products...' : options.length ? 'Not chosen yet' : 'No products match'
        }
        disabled={disabled || (!options.length && !value)}
        helpText={helpText}
      />
    </div>
  );
};

/**
 * What may go in a JSON column, on demand.
 *
 * The reference lives in `support/labSpecSchema` alongside the descriptions the guided forms are
 * built from, so the documentation and the controls cannot drift: a key added to the schema shows up
 * in both or neither. Closed rather than printed above the field, because the reference for a step's
 * action runs to twenty-one keys and a form that showed all of that beside every textarea would be
 * unreadable in a different way from the one it is fixing.
 *
 * Each key states what happens when it is left out. That is the part worth reading - the failures
 * these columns produce are the quiet ones, where a step with no expectation passes whatever
 * happened and one with no blast radius is silently excluded from every fleet sweep.
 */
export const LabHelpMenu: React.FC<{
  schema: LabJsonSchema;
  /** Replaces whatever is in the field with an example. Omitted where there is nothing to write to. */
  onUseExample?: (value: Record<string, any>) => void;
  /** Whether the field already holds something, so the button can say it will overwrite. */
  occupied?: boolean;
}> = ({ schema, onUseExample, occupied = false }) => {
  const [open, setOpen] = useState(false);

  const documented = useMemo(
    () => (schema.groups || []).flatMap((group) => group.fields).filter((field) => field.key),
    [schema.groups],
  );

  return (
    <div className="rounded border border-gray-200 bg-gray-50">
      <button
        type="button"
        className="flex w-full items-center gap-2 border-0 bg-transparent px-2 py-1.5 text-left text-xs text-gray-700"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <HbrIcon asset={open ? caretDown : caretRight} className="text-sm text-gray-500" />
        <HbrIcon asset={question} className="text-sm text-gray-500" />
        {/* The count is part of the question: a column with three documented keys should not
            look as daunting as one with twenty. */}
        <span className="font-medium">
          {documented.length
            ? `What can go in ${schema.title.toLowerCase()}? ${documented.length} key${
                documented.length === 1 ? '' : 's'
              }`
            : `What can go in ${schema.title.toLowerCase()}?`}
        </span>
      </button>

      {open && (
        <div className="flex flex-col gap-3 border-t border-gray-200 px-3 py-2 text-xs text-gray-700">
          <p className="m-0">{schema.summary}</p>

          {schema.freeform && <p className="m-0 text-gray-600">{schema.freeform.help}</p>}

          {!!documented.length && (
            <dl className="m-0 flex flex-col gap-1.5">
              {documented.map((field) => (
                <div className="grid grid-cols-[minmax(0,10rem)_1fr] gap-2" key={field.key}>
                  <dt className="m-0 font-mono text-[11px] text-gray-800">{field.key}</dt>
                  <dd className="m-0">
                    {field.help}
                    {field.options && (
                      <span className="block text-gray-500">
                        {`One of: ${field.options.map((option) => option.value).join(', ')}.`}
                      </span>
                    )}
                    {field.fallback && (
                      <span className="block text-gray-500">{`Left out: ${field.fallback}`}</span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          {!!schema.notes?.length && (
            <ul className="m-0 list-disc pl-4 text-gray-600">
              {schema.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}

          {!!schema.examples?.length && (
            <div className="flex flex-col gap-2">
              <span className="font-medium text-gray-700">Examples</span>
              {schema.examples.map((example) => (
                <div className="rounded border border-gray-200 bg-white p-2" key={example.label}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium">{example.label}</span>
                    {onUseExample && (
                      <StbButton
                        role="default"
                        size="xsmall"
                        onClick={async () => onUseExample(example.value)}
                      >
                        {occupied ? 'Replace with this' : 'Use this'}
                      </StbButton>
                    )}
                  </div>
                  {example.hint && <p className="m-0 mt-0.5 text-gray-500">{example.hint}</p>}
                  <pre className="m-0 mt-1 overflow-auto font-mono text-[11px] text-gray-700">
                    {JSON.stringify(example.value, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * A JSON column, edited as text.
 *
 * These fields carry step payloads, expectations and precondition scopes, and the backend
 * validators read them as objects. Text that does not parse is refused here rather than
 * sent, because a 400 from the generic CRUD API says "invalid" and not "line 4".
 *
 * `schema` adds the reference and its worked examples. Every call site that edits a documented
 * column passes one: knowing that a field takes JSON is not the same as knowing what to put in it,
 * and an empty textarea answers neither question.
 */
export const LabJsonField: React.FC<{
  label: string;
  value: Record<string, any> | null | undefined;
  onChange: (value: Record<string, any> | null, error: string | null) => void;
  helpText?: string;
  rows?: number;
  placeholder?: string;
  /** The documented shape of this column, from `support/labSpecSchema`. */
  schema?: LabJsonSchema;
  /**
   * Changes when a different row is loaded into the form. Needed because the modals stay
   * mounted between rows: without it, opening step B would show step A's payload.
   */
  resetKey?: string | number;
}> = ({ label, value, onChange, helpText, rows = 5, placeholder, schema, resetKey }) => {
  const [text, setText] = useState<string>(() =>
    value === null || value === undefined ? '' : JSON.stringify(value, null, 2),
  );
  const [error, setError] = useState<string | null>(null);

  // Re-seed when the row being edited changes, and only then - reformatting text under
  // somebody's cursor on every keystroke is unusable.
  useEffect(() => {
    setText(value === null || value === undefined ? '' : JSON.stringify(value, null, 2));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, resetKey]);

  const handle = (next: string) => {
    setText(next);

    if (!next.trim()) {
      setError(null);
      onChange(null, null);
      return;
    }

    try {
      const parsed = JSON.parse(next);

      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        const message = 'This has to be a JSON object.';
        setError(message);
        onChange(null, message);
        return;
      }

      setError(null);
      onChange(parsed, null);
    } catch (exc) {
      const message = (exc as Error).message || 'That is not valid JSON.';
      setError(message);
      onChange(null, message);
    }
  };

  return (
    <div className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-gray-700">{label}</span>

      {schema && (
        <LabHelpMenu
          schema={schema}
          occupied={!!text.trim()}
          // Written through `handle` rather than straight to state, so an example lands in the
          // draft the same way typing would and the field cannot show one thing and save another.
          onUseExample={(example) => handle(JSON.stringify(example, null, 2))}
        />
      )}

      <textarea
        className={`w-full rounded border p-2 font-mono text-xs ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
        rows={rows}
        value={text}
        placeholder={placeholder}
        spellCheck={false}
        onChange={(evt) => handle(evt.target.value)}
      />
      {error ? (
        <span className="text-xs text-red-700">{error}</span>
      ) : (
        helpText && <span className="text-xs text-gray-500">{helpText}</span>
      )}
    </div>
  );
};

/** Read-only JSON, for output and evidence the agent reported. */
export const LabJsonView: React.FC<{ value: any; className?: string }> = ({
  value,
  className = '',
}) => {
  if (value === null || value === undefined) return <span className="text-gray-500">-</span>;

  return (
    <pre
      className={`m-0 max-h-64 overflow-auto rounded bg-gray-50 p-2 font-mono text-xs ${className}`}
    >
      {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
};

export const LabCheckbox: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  helpText?: string;
  disabled?: boolean;
}> = ({ label, checked, onChange, helpText, disabled }) => (
  <label className="flex items-start gap-2 text-sm">
    <input
      type="checkbox"
      className="mt-1"
      checked={checked}
      disabled={disabled}
      onChange={(evt) => onChange(evt.target.checked)}
    />
    <span>
      <span className="font-medium text-gray-700">{label}</span>
      {helpText && <span className="block text-xs text-gray-500">{helpText}</span>}
    </span>
  </label>
);

/**
 * A yes/no that has no default.
 *
 * `disruptive` is nullable on purpose: unset means nobody has judged it, and the injection
 * validator refuses to push a step in that state. A checkbox cannot represent that, so this
 * is three states and starts on the third.
 */
export const LabTristate: React.FC<{
  label: string;
  value: boolean | null | undefined;
  onChange: (value: boolean | null) => void;
  helpText?: string;
  yesLabel?: string;
  noLabel?: string;
}> = ({ label, value, onChange, helpText, yesLabel = 'Yes', noLabel = 'No' }) => (
  <div className="flex flex-col gap-1 text-sm">
    <span className="font-medium text-gray-700">{label}</span>
    <div className="flex flex-wrap gap-2">
      {[
        { key: 'yes', label: yesLabel, next: true as boolean | null },
        { key: 'no', label: noLabel, next: false as boolean | null },
        { key: 'unset', label: 'Not stated', next: null },
      ].map((choice) => {
        const active =
          (choice.key === 'unset' && (value === null || value === undefined)) ||
          (choice.key !== 'unset' && value === choice.next);

        return (
          <button
            key={choice.key}
            type="button"
            className={`rounded-full border px-3 py-1 text-sm ${
              active ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => onChange(choice.next)}
          >
            {choice.label}
          </button>
        );
      })}
    </div>
    {helpText && <span className="text-xs text-gray-500">{helpText}</span>}
  </div>
);

/** The reasons an action is unavailable, spelled out next to the disabled button. */
export const LabBlockerList: React.FC<{ blockers: string[]; heading?: string }> = ({
  blockers,
  heading = 'Not ready:',
}) => {
  if (!blockers.length) return null;

  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
      <div className="font-medium">{heading}</div>
      <ul className="mb-0 mt-1 list-disc pl-5">
        {blockers.map((blocker) => (
          <li key={blocker}>{blocker}</li>
        ))}
      </ul>
    </div>
  );
};
